import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import classnames from 'classnames'

import useDeferredCaptchaSubmit from 'hooks/useDeferredCaptchaSubmit'
import getErrorMessage from 'reduxModules/utils/getErrorMessage'
import notifications from '../../../global/notifications'
import EMAIL_PATTERN from '../../utils/emailPattern'
import { CaptchaNotice } from '../CaptchaNotice'
import CaptchaStandalone from '../../CaptchaStandalone/_lazy'
import CodeInput from '../CodeInput'
import InlineError, { ariaErrorProps, errorId } from '../InlineError'
import '../RenderedField/styles.css'
import './styles.css'

const RESEND_COOLDOWN_SECONDS = 60
const CODE_LENGTH = 6

/**
 * One-time code login box (SPEC-041), rendered by the /login/otp page.
 *
 * Two steps, both held in component state so a refresh always lands on the
 * first one and nothing is ever sent on mount:
 *   confirm — an email field (prefilled when the flow brought one along) and
 *             a Send code button. The request is Turnstile-gated: the button
 *             waits for the widget's token rather than capturing the click.
 *   enter   — takes the 6-digit code in a six-cell input that submits on
 *             its own once the last digit lands (typed, pasted, or autofilled).
 *             Success resolves the standard login-success action, so
 *             post-login handling (checkout continuity included) is the same
 *             as a password login.
 */
export function OtpEntry({
  email = '',
  recaptcha,
  sendOtp,
  verifyCode,
  children
}) {
  const [step, setStep] = useState('confirm')
  // The address the current code went to: shown on the entry step and used
  // to verify, so editing the field afterwards can't desync the two.
  const [sentTo, setSentTo] = useState(email)
  // Seconds left before "Resend code" unlocks; advisory only — the backend
  // enforces the real per-email cooldown.
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [code, setCode] = useState('')
  const [verifyError, setVerifyError] = useState(null)
  const [verifying, setVerifying] = useState(false)

  const requestForm = useForm({ mode: 'onChange', defaultValues: { email } })
  const emailValue = requestForm.watch('email')
  const emailOk = EMAIL_PATTERN.test(emailValue || '')

  const requestCode = ({ email: address }) =>
    sendOtp(address).then((sent) => {
      if (sent) {
        setSentTo(address)
        setStep('enter')
        setSecondsLeft(RESEND_COOLDOWN_SECONDS)
      }
      return sent
    })

  const { submit, awaitingVerification, isSubmitting } =
    useDeferredCaptchaSubmit(recaptcha?.token, requestCode)
  const sending = isSubmitting || awaitingVerification
  // Both request buttons wait for Turnstile to pass rather than capturing an
  // early click: the widget sits right above them, so a disabled button until
  // it turns green is the honest state. The token is consumed by each request
  // and re-issued by the widget, so Resend re-locks until the next one lands.
  const verified = Boolean(recaptcha?.token)

  useEffect(() => {
    if (secondsLeft <= 0) return undefined
    const timer = setInterval(
      () => setSecondsLeft((seconds) => (seconds > 0 ? seconds - 1 : 0)),
      1000
    )
    return () => clearInterval(timer)
  }, [secondsLeft > 0])

  // Runs from the sixth digit landing or from the Log in button; a wrong
  // code keeps the digits in place, flagged, so they can be corrected.
  const verify = (value) => {
    if (verifying || value.length !== CODE_LENGTH) return undefined
    setVerifyError(null)
    setVerifying(true)
    // Start inside a promise so a synchronous throw from the exchange still
    // lands in .catch/.finally and can't strand the button on "Verifying…".
    return Promise.resolve()
      .then(() => verifyCode(sentTo, value))
      .then((result) => {
        if (!result.success)
          setVerifyError(
            getErrorMessage(
              result.payload,
              notifications.otpVerify.error.message
            )
          )
      })
      .catch(() => setVerifyError(notifications.otpVerify.error.message))
      .finally(() => setVerifying(false))
  }

  const onVerifySubmit = (event) => {
    event.preventDefault()
    verify(code)
  }

  const handleCodeChange = (digits) => {
    setCode(digits)
    if (verifyError) setVerifyError(null)
  }

  const goBack = () => {
    setVerifyError(null)
    setCode('')
    setStep('confirm')
  }

  const requestErrors = requestForm.formState.errors

  return (
    <div id="OtpEntry" role="region" aria-label="One-time code login">
      <div className="otp-box auth-form-box">
        {step === 'confirm' && (
          <>
            <h1 className="otp-title">Log in with a code</h1>
            <p className="otp-subtitle">
              We&apos;ll email you a one-time 6-digit code.
            </p>
            <form onSubmit={requestForm.handleSubmit(submit)} noValidate>
              <div
                className={classnames('field', '-floating', {
                  '-error': requestErrors.email
                })}
              >
                <div className="field-container">
                  <input
                    {...requestForm.register('email', {
                      required: 'This field is required.',
                      pattern: {
                        value: EMAIL_PATTERN,
                        message: 'Invalid email address.'
                      }
                    })}
                    id="otp-email"
                    type="email"
                    placeholder=" "
                    autoFocus={!email}
                    {...ariaErrorProps('otp-email', requestErrors.email)}
                  />
                  <label htmlFor="otp-email">Email</label>
                  <InlineError
                    id="otp-email"
                    message={requestErrors.email?.message}
                  />
                </div>
              </div>
              <CaptchaStandalone />
              <button
                type="submit"
                className="btn -wide -blue-2 otp-submit"
                disabled={!emailOk || sending || !verified}
              >
                {sending && <span className="otp-spinner" aria-hidden="true" />}
                {sending ? 'Sending…' : 'Send code'}
              </button>
            </form>
          </>
        )}
        {step === 'enter' && (
          <>
            <button
              type="button"
              className="otp-back"
              onClick={goBack}
              // A resend in flight would land on setStep('enter') and undo
              // the back navigation, so hold Back until it settles.
              disabled={sending}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                  fill="currentColor"
                  d="M15.4 5.6 9 12l6.4 6.4-1.4 1.4L6.2 12l7.8-7.8z"
                />
              </svg>
              Back
            </button>
            <h1 className="otp-title">Check your email</h1>
            <p className="otp-subtitle">
              Enter the 6-digit code we sent to <strong>{sentTo}</strong>.
            </p>
            <form onSubmit={onVerifySubmit} noValidate>
              <div className="otp-code-field">
                <label htmlFor="otp-code" className="field__label--hidden">
                  6-digit code
                </label>
                <CodeInput
                  id="otp-code"
                  length={CODE_LENGTH}
                  value={code}
                  onChange={handleCodeChange}
                  onComplete={verify}
                  invalid={Boolean(verifyError)}
                  disabled={verifying}
                  autoFocus
                  describedBy={errorId('otp-code')}
                />
                <InlineError id="otp-code" message={verifyError} />
              </div>
              <CaptchaStandalone />
              <button
                type="submit"
                className="btn -wide -blue-2 otp-submit"
                disabled={code.length !== CODE_LENGTH || verifying}
              >
                {verifying ? 'Verifying…' : 'Log in'}
              </button>
            </form>
            <div className="otp-actions">
              <button
                type="button"
                className="otp-resend"
                onClick={() => submit({ email: sentTo })}
                disabled={secondsLeft > 0 || sending || !verified}
              >
                {secondsLeft > 0 ? `Resend code (${secondsLeft}s)` : 'Resend code'}
              </button>
            </div>
          </>
        )}
        {step === 'confirm' && children}
        <CaptchaNotice dark />
      </div>
    </div>
  )
}

OtpEntry.propTypes = {
  email: PropTypes.string,
  recaptcha: PropTypes.shape({
    token: PropTypes.string,
    version: PropTypes.string
  }),
  sendOtp: PropTypes.func.isRequired,
  verifyCode: PropTypes.func.isRequired,
  children: PropTypes.node
}

export default OtpEntry
